export const VALIDATION_RULES = {
  required: (v) => Boolean(String(v ?? '').length) || 'Required field',
  requiredArray: (v) => Boolean(v.length) || 'Required field',
  email: (v) => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'Invalid email',
  min: (min) => (v) => v >= min || `Value mustn't be less ${min}`,
  max: (max) => (v) => v <= max || `Value mustn't be more ${max}`,
  number: (v) => typeof v === 'number' || 'Must be number',
  maxLength: (max) => (v) => (v && v.length <= max) || `Field cannot contain more than ${max} characters`,
  decimalPlacesCount: (v) => /^[0-9]*[.]?\d{1,2}$/.test(v) || 'Must be no more than two decimal places',
  combobox: (v) => typeof v !== 'string' || 'Select value from list',
  positiveNumber: (v) => v >= 0 || 'Value mustn\'t be negative',
  alreadyTaken: (values) => (v) => !values.includes(v) || 'Value must be unique',
};

const getRuleNameFromConfig = (config) => (typeof config === 'string' ? config : config.rule);

const generateRule = (rule) => {
  if (typeof rule === 'string' && VALIDATION_RULES[rule]) {
    return VALIDATION_RULES[rule];
  }

  if (typeof rule === 'object' && VALIDATION_RULES[rule.rule]) {
    return VALIDATION_RULES[rule.rule](rule.value);
  }

  return false;
};

const wrapRuleToOptionalChecker = (optional, rule) => (v) => {
  if (!optional || Boolean(String(v ?? '').length)) {
    return rule(v);
  }
  return true;
};

const generateValidatorsByConfig = (config) => Object.keys(config)
  .reduce((acc, field) => {
    acc[field] = config[field]
      .reduce((accRules, ruleConfig) => {
        const ruleName = getRuleNameFromConfig(ruleConfig);
        const rule = generateRule(ruleConfig);
        if (rule) {
          accRules[ruleName] = wrapRuleToOptionalChecker(config[field].includes('optional'), rule);
        }
        return accRules;
      }, {});

    return acc;
  }, {});

export const Validator = {
  createValidator: (config) => {
    let rules = generateValidatorsByConfig(config);

    return {
      getRulesByField: (fieldName) => (rules[fieldName] ? Object.values(rules[fieldName]) : []),
      addFieldRule: (fieldName, rule) => {
        if (rules[fieldName]) {
          rules[fieldName].push(rule);
        } else {
          rules[fieldName] = [rule];
        }
      },
      updateFieldRule: (fieldName, ruleConfig, optional = false) => {
        if (!rules[fieldName]) {
          return new Error(`No available field ${fieldName}`);
        }

        const ruleName = getRuleNameFromConfig(ruleConfig);

        if (!rules[fieldName][ruleName]) {
          return new Error(`No available rule ${ruleName}`);
        }

        const rule = generateRule(ruleConfig);

        rules[fieldName][ruleName] = wrapRuleToOptionalChecker(optional, rule);

        return true;
      },
      updateFieldRules: (fieldName, c) => {
        if (!rules[fieldName]) {
          return new Error(`No available field ${fieldName}`);
        }
        rules[fieldName] = c.reduce((acc, ruleConfig) => {
          const ruleName = getRuleNameFromConfig(ruleConfig);
          const rule = generateRule(ruleConfig);
          if (rule) {
            acc[ruleName] = wrapRuleToOptionalChecker(c.includes('optional'), rule);
          }
          return acc;
        }, {});
        return true;
      },
      removeFieldRule: (fieldName, ruleName) => {
        if (!rules[fieldName]) {
          return new Error(`No available field ${fieldName}`);
        }
        if (!rules[fieldName][ruleName]) {
          return new Error(`No available rule ${ruleName}`);
        }

        delete rules[fieldName][ruleName];

        return true;
      },
      rebuildValidator: (c) => {
        rules = generateValidatorsByConfig(c);
      },
    };
  },
  useValidator: (validator) => ({
    computed: {
      validator: () => validator,
    },
  }),
  generateRule: ({ rule, config, optional = false }) => {
    const r = rule || generateRule(config);
    if (!r) {
      return new Error('Cannot create rule');
    }
    return wrapRuleToOptionalChecker(optional, r);
  },
};
